// =====================
// Types

import { dateToDateString } from "../../etc/etcfunctions";

// =====================
export type PayInfo = {
  id: number;
  userId: number;
  location: string;
  price: number; // "월 단가"
  year: number;  // ❌ 무시
  month: number; // ❌ 무시
  createdAt: string;
};

export type DateRange = [Date, Date];

// =====================
// Utils
// =====================
const daysInMonth = (year: number, month1to12: number) =>
  new Date(year, month1to12, 0).getDate();

/** 10원 단위 절사 */
const floorToTens = (n: number) => Math.floor(n / 10) * 10;

/** 날짜 비교용 day number */
const dayNumber = (d: Date) =>
  Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);

/** 포함 일수 (양 끝 포함) */
const inclusiveDays = (start: Date, end: Date) =>
  dayNumber(end) - dayNumber(start) + 1;

const startOfMonth = (y: number, m0: number) => new Date(y, m0, 1);
const endOfMonth = (y: number, m0: number) =>
  new Date(y, m0, daysInMonth(y, m0 + 1));

const intersectRange = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) => {
  const s = dayNumber(aStart) > dayNumber(bStart) ? aStart : bStart;
  const e = dayNumber(aEnd) < dayNumber(bEnd) ? aEnd : bEnd;
  if (dayNumber(s) > dayNumber(e)) return null;
  return { start: s, end: e };
};

// =====================
// Main
// =====================
/**
 * location별 "월 단가(price)" 기준으로
 * 선택 기간 전체를 월 단위로 나눠 일할 계산 후 합산
 *
 * - year/month 무시
 * - 모든 달에 동일한 price 적용
 * - 월 전체 포함 → price 그대로
 * - 일부만 포함 → (price / 해당월 일수) * 포함일수
 */
export function calcRegisterFeeByLocation(params: {
  registerPayInfo: PayInfo[];
  selectedRange: DateRange;
  appliedDiscount?: number;
}) {
  const { registerPayInfo, selectedRange, appliedDiscount = 0 } = params;
  const [start, end] = selectedRange;

  const discountFactor = (100 - appliedDiscount) / 100;

  if (dayNumber(start) > dayNumber(end)) {
    throw new Error("selectedRange start is after end");
  }

  // 1) location별 월 단가 맵 (중복 있으면 마지막 값 사용)
  const priceByLocation: Record<string, number> = {};
  for (const p of registerPayInfo) {
    //소수점 버림 적용
     const discountedPrice =  Math.floor(p.price * discountFactor / 10) * 10;
     priceByLocation[p.location] = discountedPrice;
  }

  const result: Record<string, number> = {};

  // 2) location별 계산
  for (const [location, monthlyPrice] of Object.entries(priceByLocation)) {
    let sum = 0;

    let cursor = startOfMonth(start.getFullYear(), start.getMonth());
    const endCursorLimit = endOfMonth(end.getFullYear(), end.getMonth());

    while (dayNumber(cursor) <= dayNumber(endCursorLimit)) {
      const y = cursor.getFullYear();
      const m0 = cursor.getMonth();

      const monthStart = startOfMonth(y, m0);
      const monthEnd = endOfMonth(y, m0);

      const hit = intersectRange(start, end, monthStart, monthEnd);
      if (hit) {
        const totalDays = daysInMonth(y, m0 + 1);
        const included = inclusiveDays(hit.start, hit.end);

        const amount =
          included === totalDays
            ? monthlyPrice
            : floorToTens((monthlyPrice / totalDays) * included);

        sum += amount;
      }

      // 다음 달
      cursor = new Date(y, m0 + 1, 1);
    }

    result[location] = sum;
  }

  return {
    start,
    end,
    result, // { gangnam: xxxx, daechi: xxxx, songpa: xxxx }
  };
}


export function calcRegisterFee(params: {
  registerPayInfo: PayInfo[];
  selectedRange: DateRange;
  appliedDiscount?: number;
}) {
  const { registerPayInfo, selectedRange, appliedDiscount = 0 } = params;
  const [start, end] = selectedRange;

  const discountFactor = (100 - appliedDiscount) / 100;

  if (dayNumber(start) > dayNumber(end)) {
    throw new Error("selectedRange start is after end");
  }

  // 1) location별 월 단가 맵 (중복 있으면 마지막 값 사용)
  const priceByLocation: Record<string, number> = {};
  for (const p of registerPayInfo) {
    //소수점 버림 적용
     const discountedPrice =  Math.floor(p.price * discountFactor / 10) * 10;
     priceByLocation[p.location] = discountedPrice;
  }

  const result: Record<string, number> = {};

  // 2) location별 계산
  for (const [location, monthlyPrice] of Object.entries(priceByLocation)) {
    let sum = 0;

    let cursor = startOfMonth(start.getFullYear(), start.getMonth());
    const endCursorLimit = endOfMonth(end.getFullYear(), end.getMonth());

    while (dayNumber(cursor) <= dayNumber(endCursorLimit)) {
      const y = cursor.getFullYear();
      const m0 = cursor.getMonth();

      const monthStart = startOfMonth(y, m0);
      const monthEnd = endOfMonth(y, m0);

      const hit = intersectRange(start, end, monthStart, monthEnd);
      if (hit) {
        const totalDays = daysInMonth(y, m0 + 1);
        const included = inclusiveDays(hit.start, hit.end);

        const amount =
          included === totalDays
            ? monthlyPrice
            : floorToTens((monthlyPrice / totalDays) * included);

        sum += amount;
      }

      // 다음 달
      cursor = new Date(y, m0 + 1, 1);
    }

    result[location] = sum;
  }

  return {
    start,
    end,
    result, // { gangnam: xxxx, daechi: xxxx, songpa: xxxx }
  };
}



type ResultMap = Record<string, number>;

const formatWon = (n: number) => `${n.toLocaleString("ko-KR")}원`;

// englishLocationToKorean: (loc: string) => string
export const makeGroupedFeeLines = (params: {
  result: ResultMap;
  englishLocation: string[];
  appliedDiscount?: number;
  englishLocationToKorean: (loc: string) => any;
}) => {
  const { result, englishLocation, appliedDiscount = 0, englishLocationToKorean } = params;

  var discountString = `${appliedDiscount ? `(${appliedDiscount}% 할인 적용)` : ''}`;

  // amount(string) -> koreanNames[]
  const group: Record<string, string[]> = {};

  for (let i = 0; i < englishLocation.length; i++) {
    const loc = (englishLocation[i] ?? "").trim();
    const amount = result[loc];
    if (amount == null) continue;

    const koName = englishLocationToKorean(loc) || loc;
    const key = String(amount);

    if (!group[key]) group[key] = [];
    group[key].push(koName);
  }

  // 중복 제거 + 정렬
  Object.keys(group).forEach((key) => {
    group[key] = Array.from(new Set(group[key])).sort((a, b) =>
      a.localeCompare(b, "ko")
    );
  });

  // 금액 큰 순으로 라인 생성
  const lines = Object.keys(group)
    .map((key) => Number(key))
    .sort((a, b) => b - a)
    .map((amt) => `${group[String(amt)].join(", ")} : ${formatWon(amt)} ${discountString}`);

  return lines;
};


export const orderItemsDataUpdatedDataParse = (data : any, teacherList : any[]) => {

    const actions : any = {
        insert : "추가",
        changePrice : "금액 수동 변경",
        changeDate : "날짜 수동 변경",
    }

    data.forEach((item : any) => {

        if(item.updatedDataOI){

            item.updatedDataOI.forEach((oiItem : any) => {

                const selectedTeacher = teacherList.find((teacher : any) => teacher.id === oiItem.teacherId);

                if(!selectedTeacher){
                    return;
                }

                oiItem.teacherName = selectedTeacher.name;

                const createdAtDate = new Date(+oiItem.updatedAt);
                oiItem.updatedAtFormatted = dateToDateString(createdAtDate, true);

                oiItem.actionText = actions[oiItem.action] || oiItem.action;

            });

        }

        if(item.updatedDataORI){

            item.updatedDataORI.forEach((oriItem : any) => {

                const selectedTeacher = teacherList.find((teacher : any) => teacher.id === oriItem.teacherId);

                if(!selectedTeacher){
                    return;
                }

                oriItem.teacherName = selectedTeacher.name;

                const createdAtDate = new Date(+oriItem.updatedAt);
                oriItem.updatedAtFormatted = dateToDateString(createdAtDate, true);

                oriItem.actionText = actions[oriItem.action] || oriItem.action;

            });

        }

    })

}

export const connectOrderItemsDataToUser = (userData : any[], orderItemsData : any[]) => {

    userData.forEach((user : any) => {

        var unpaidOrderItem : any = undefined;
        const paidOrderItems : any[] = [];

        orderItemsData.forEach((orderItem : any) => {

            if(orderItem.userId === user.id){
                        
                if(orderItem.isPaid === 0 && unpaidOrderItem === undefined){
                    unpaidOrderItem = orderItem;
                }else if(orderItem.isPaid === 1){
                    paidOrderItems.push(orderItem);
                }

            }

        });

        user.unpaidOrderItem = unpaidOrderItem;
        user.paidOrderItems = paidOrderItems;

    });

}

const toDigitString = (num: number, wantedLenght: number) => {
  return String(num).padStart(wantedLenght, "0");
}

export const makeBillId = (businessNumber: string, id: number) => {
    var bill_id = businessNumber;

    bill_id = bill_id + "9" + toDigitString(id, 9);

    return bill_id;
}
